<template>
  <div class="route-map-wrapper">
    <div v-if="loading" class="map-overlay">
      <v-progress-circular indeterminate color="primary" />
      <span class="ml-2">Calcul de l'itinéraire…</span>
    </div>
    <div v-if="error" class="map-overlay map-error">
      <v-icon color="error">mdi-alert-circle-outline</v-icon>
      <span class="ml-2">{{ error }}</span>
    </div>
    <v-btn
      class="drawer-toggle"
      color="primary"
      size="small"
      variant="elevated"
      @click="isDirectionDrawerOpen = !isDirectionDrawerOpen"
    >
      <v-icon start>{{ isDirectionDrawerOpen ? "mdi-chevron-right" : "mdi-chevron-left" }}</v-icon>
      Itineraire
    </v-btn>
    <transition name="slide-right">
      <div v-if="isDirectionDrawerOpen" class="route-drawer">
        <div class="route-drawer-header">
          <strong>Resume de tournee</strong>
        </div>
        <div v-if="taskListDirectionLoading" class="route-drawer-state">
          <v-progress-circular indeterminate size="18" color="primary" />
          <span>Chargement des directions...</span>
        </div>
        <template v-else-if="taskListDirection">
          <div class="route-summary">
            <div>
              <span class="label">Distance totale</span>
              <strong>{{ taskListDirection.totalDistance || "-" }}</strong>
            </div>
            <div>
              <span class="label">Duree totale</span>
              <strong>{{ taskListDirection.totalDuration || "-" }}</strong>
            </div>
          </div>
          <v-divider class="my-2" />
          <div class="route-steps">
            <div
              v-for="(stop, index) in taskListDirection.stops"
              :key="`${index}-${stop.from}-${stop.to}`"
              class="route-step"
            >
              <div class="route-step-title">{{ stop.from }} -> {{ stop.to }}</div>
              <div class="route-step-meta">{{ stop.distance || "-" }} | {{ stop.duration || "-" }}</div>
            </div>
            <div v-if="taskListDirection.stops.length === 0" class="route-drawer-state">
              Aucun troncon retourne.
            </div>
          </div>
        </template>
        <div v-else class="route-drawer-state">
          Directions indisponibles pour cette tournee.
        </div>
      </div>
    </transition>
    <div ref="mapContainer" class="map-container" />
  </div>
</template>

<script setup lang="ts">
import type { GeoAddress } from "@/types/geo-address";
import type { Task } from "@/types/task";
import type { TaskListDirection } from "@/types/task-list-direction";
import { geocodeAddressesBatch } from "@/utils/geocode-api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  startAddress: GeoAddress;
  endAddress: GeoAddress;
  tasks: Task[];
  taskListDirection: TaskListDirection | null;
  taskListDirectionLoading: boolean;
}>();

const mapContainer = ref<HTMLElement | null>(null);
const loading = ref(false);
const error = ref("");
const isDirectionDrawerOpen = ref(true);

let mapInstance: L.Map | null = null;

function formatStart(a: GeoAddress): string {
  return `${a.line1 ?? ""} ${a.line2 ?? ""}, ${a.postalCode ?? ""} ${a.city ?? ""}`.trim();
}

function formatEnd(a: GeoAddress): string {
  return `${a.line1 ?? ""} ${a.line2 ?? ""}, ${a.postalCode ?? ""} ${a.city ?? ""}`.trim();
}

function formatTask(t: Task): string {
  return `${t.number ?? ""} ${t.street ?? ""}, ${t.postcode ?? ""} ${t.city ?? ""}`.trim();
}

function formatTaskFallback(t: Task): string {
  return `${t.postcode ?? ""} ${t.city ?? ""}`.trim();
}

function formatStartFallback(a: GeoAddress): string {
  return `${a.postalCode ?? ""} ${a.city ?? ""}`.trim();
}

function formatEndFallback(a: GeoAddress): string {
  return `${a.postalCode ?? ""} ${a.city ?? ""}`.trim();
}

function getGeoAddressCoordinates(address: GeoAddress): [number, number] | null {
  if (typeof address.latitude === "number" && typeof address.longitude === "number") {
    return [address.latitude, address.longitude];
  }

  return null;
}

function makeIcon(label: string, color: string): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<div style="
      background:${color};
      color:#fff;
      border-radius:50%;
      width:26px;
      height:26px;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:12px;
      font-weight:bold;
      border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,.4);
    ">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function getMarkerPosition(
  coordinate: [number, number],
  markerCountByCoord: Map<string, number>
): [number, number] {
  const key = `${coordinate[0].toFixed(6)},${coordinate[1].toFixed(6)}`;
  const markerCount = markerCountByCoord.get(key) ?? 0;
  markerCountByCoord.set(key, markerCount + 1);

  if (markerCount === 0) {
    return coordinate;
  }

  const offset = 0.00012;
  const angle = markerCount * (Math.PI / 3);
  return [
    coordinate[0] + Math.cos(angle) * offset,
    coordinate[1] + Math.sin(angle) * offset,
  ];
}

async function buildMap() {
  if (!mapContainer.value) return;

  console.log('[RouteMap] buildMap called, tasks count:', props.tasks.length);
  console.log('[RouteMap] tasks:', JSON.parse(JSON.stringify(props.tasks)));
  console.log('[RouteMap] startAddress:', props.startAddress);
  console.log('[RouteMap] endAddress:', props.endAddress);

  loading.value = true;
  error.value = "";

  // Destroy existing instance
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }

  // Init map
  mapInstance = L.map(mapContainer.value, { zoomControl: true });

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(mapInstance);

  // Collect stops: start → tasks in rank order → end
  type Stop = {
    label: string;
    address: string;
    fallbackAddress?: string;
    color: string;
    strikethrough?: boolean;
    name?: string;
    coordinates?: [number, number];
  };
  const stops: Stop[] = [
    {
      label: "D",
      address: formatStart(props.startAddress),
      fallbackAddress: formatStartFallback(props.startAddress),
      color: "#43a047",
      coordinates: getGeoAddressCoordinates(props.startAddress) ?? undefined,
    },
    ...props.tasks
      .slice()
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map((t, i) => ({
        label: String(i + 1),
        address: formatTask(t),
        fallbackAddress: formatTaskFallback(t),
        color: t.taskStatus === "FAITE" ? "#9e9e9e" : "#1565c0",
        strikethrough: t.taskStatus === "FAITE",
        name: t.name,
        coordinates:
          typeof t.latitude === "number" && typeof t.longitude === "number"
            ? ([t.latitude, t.longitude] as [number, number])
            : undefined,
      })),
    {
      label: "A",
      address: formatEnd(props.endAddress),
      fallbackAddress: formatEndFallback(props.endAddress),
      color: "#e53935",
      coordinates: getGeoAddressCoordinates(props.endAddress) ?? undefined,
    },
  ];

  // Geocode stops missing coordinates via one backend batch call
  const coords: ([number, number] | null)[] = stops.map((stop) => stop.coordinates ?? null);
  const missingStopIndexes: number[] = [];
  const missingStopAddresses: string[] = [];

  stops.forEach((stop, index) => {
    if (!coords[index]) {
      missingStopIndexes.push(index);
      missingStopAddresses.push(stop.address);
    }
  });

  if (missingStopAddresses.length > 0) {
    const geocodedMissingStops = await geocodeAddressesBatch(missingStopAddresses);
    geocodedMissingStops.forEach((coordinate, batchIndex) => {
      const stopIndex = missingStopIndexes[batchIndex];
      if (typeof stopIndex === "number") {
        coords[stopIndex] = coordinate;
      }
    });
  }

  // Ensure start/end are still visible if geocoding fails by falling back to nearest known stop
  const firstTaskIndex = stops.findIndex((s) => s.label === "1");
  const lastTaskIndex = stops.findLastIndex((s) => /^\d+$/.test(s.label));
  const firstTaskCoord = firstTaskIndex >= 0 ? coords[firstTaskIndex] : null;
  const lastTaskCoord = lastTaskIndex >= 0 ? coords[lastTaskIndex] : null;

  if (!coords[0]) {
    coords[0] = firstTaskCoord ?? coords[coords.length - 1] ?? null;
  }

  if (!coords[coords.length - 1]) {
    coords[coords.length - 1] = lastTaskCoord ?? coords[0] ?? null;
  }

  const validPoints: L.LatLngTuple[] = [];
  const markerCountByCoord = new Map<string, number>();

  stops.forEach((stop, i) => {
    const c = coords[i];
    if (!c) return;
    validPoints.push(c);
    const markerPosition = getMarkerPosition(c, markerCountByCoord);

    const title = stop.name
      ? `<b style="${stop.strikethrough ? "text-decoration:line-through" : ""}">${stop.name}</b><br><small>${stop.address}</small>`
      : `<b>${stop.label === "D" ? "Départ" : "Arrivée"}</b><br><small>${stop.address}</small>`;

    L.marker(markerPosition, {
      icon: makeIcon(stop.label, stop.color),
      zIndexOffset: stop.label === "D" || stop.label === "A" ? 1000 : 0,
    })
      .addTo(mapInstance!)
      .bindPopup(title);
  });

  const startPoint = coords[0] as L.LatLngTuple | null;
  const endPoint = coords[coords.length - 1] as L.LatLngTuple | null;
  const taskPoints = coords.slice(1, Math.max(1, coords.length - 1)).filter(
    (point): point is [number, number] => point !== null
  );

  const routePoints: L.LatLngTuple[] = [];
  if (startPoint && taskPoints.length > 0) {
    routePoints.push(startPoint);
  }
  routePoints.push(...taskPoints);
  if (endPoint && taskPoints.length > 0) {
    routePoints.push(endPoint);
  }

  // Draw route polyline
  if (routePoints.length >= 2) {
    L.polyline(routePoints, { color: "#1565c0", weight: 3, opacity: 0.75, dashArray: "6 4" })
      .addTo(mapInstance!);
    mapInstance!.fitBounds(L.latLngBounds(validPoints), { padding: [30, 30] });
  } else if (validPoints.length === 1) {
    mapInstance!.setView(validPoints[0], 13);
  } else {
    // Fallback: France centre
    mapInstance!.setView([46.603354, 1.888334], 6);
    error.value = "Impossible de géocoder les adresses.";
  }

  loading.value = false;
}

onMounted(() => {
  buildMap();
});

watch(
  () => [props.tasks, props.startAddress, props.endAddress],
  () => buildMap(),
  { deep: true }
);

onBeforeUnmount(() => {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }
});
</script>

<style scoped>
.route-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 260px;
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 260px;
  z-index: 0;
}

.map-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.85);
  padding: 8px 16px;
  border-radius: 8px;
  pointer-events: none;
}

.map-error {
  color: #b71c1c;
}

.drawer-toggle {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 20;
}

.route-drawer {
  position: absolute;
  top: 52px;
  right: 12px;
  width: min(360px, calc(100% - 24px));
  max-height: calc(100% - 64px);
  overflow: auto;
  z-index: 20;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e3e7ed;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(26, 32, 44, 0.18);
  padding: 10px;
}

.route-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.route-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.route-summary .label {
  display: block;
  font-size: 12px;
  color: #546e7a;
}

.route-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.route-step {
  border-left: 3px solid #1565c0;
  padding: 6px 8px;
  background: #f7fbff;
  border-radius: 6px;
}

.route-step-title {
  font-size: 13px;
  font-weight: 600;
  color: #1c2a39;
}

.route-step-meta {
  font-size: 12px;
  color: #4e6072;
}

.route-drawer-state {
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4e6072;
  font-size: 13px;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.18s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

@media (max-width: 960px) {
  .drawer-toggle {
    top: 8px;
    right: 8px;
  }

  .route-drawer {
    top: 46px;
    right: 8px;
    width: calc(100% - 16px);
  }
}
</style>
